import { h } from 'preact'
import { useState } from 'preact/hooks'
import { initI18n, i18n } from 'i18n/i18n'

import { CurrencyValueForm } from 'models/apps'
import { SpinnerError } from 'models/calculator'
import { Calculation, ExchangeDirection } from 'models/exchange-transfer-calculate'

import {
  DEFAULT_EXCHANGE_DATA,
  DEFAULT_RESPONSE,
  DISCOUNT_RATE,
  DEFAULT_MINIMAL_AMOUNT,
  SETTINGS_KEYS,
  DEFAULT_REFRESH_TIME,
  FIXED_OPTION_LIST,
  DEFAULT_DEBOUNCE_TIME,
} from '../config'

import { getCurrentLanguage } from 'utils/current-language'
import { formatCurrencyAmount } from 'app-utils/validate-currency-input'
import { validateDiscountRate } from 'app-utils/validate-discount'

import { useExchangeData } from 'hooks/useExchangeData'
import { useSettings } from 'hooks/useSettings'
import { useCurrencySelectOptions } from 'hooks/useCurrencySelectOptions'
import { useMinimalAmount } from 'hooks/useMinimalAmount'

import ErrorSpinner from 'common-modules/error-spinner'
import CalculatorActualRate from 'common-modules/exchange-calculator/actual-rate'
import CalculatorDirectionSelect from 'common-modules/exchange-calculator/direction-select'
import CalculatorInput from 'common-modules/exchange-calculator/currency-amount-input'
import CalculatorCurrencySelect from 'common-modules/exchange-calculator/currency-select'
import CalculatorFooter from 'common-modules/exchange-calculator/footer'

import CalculatorHeader from './header'

initI18n()

const MainCalculatorView = () => {
  const [direction, setDirection] = useState<ExchangeDirection>(DEFAULT_EXCHANGE_DATA.direction)
  const [baseCurrency, setBaseCurrency] = useState(DEFAULT_EXCHANGE_DATA.baseCurrency)
  const [counterCurrency, setCounterCurrency] = useState(DEFAULT_EXCHANGE_DATA.counterCurrency)
  const [value, setValue] = useState<CurrencyValueForm>(formatCurrencyAmount(DEFAULT_EXCHANGE_DATA.baseAmount))
  const [exchangeData, setExchangeData] = useState<Calculation>(DEFAULT_RESPONSE)
  const [spinnerError, setSpinnerError] = useState<SpinnerError>({
    isErrorVisible: false,
  })
  const currentLang = getCurrentLanguage()
  const [discountRate] = useState<number>(validateDiscountRate(DISCOUNT_RATE))

  const {
    isSettingsLoading,
    isCurrentCurrencySupported,
    isCurrencyPairSupported,
    setIsCurrentCurrencySupported,
    supportedCurrenciesPairs,
    allCurrencies,
    minimalAmounts,
  } = useSettings({
    setSpinnerError,
    config: {
      SETTINGS_KEYS,
      DEFAULT_MINIMAL_AMOUNT,
    },
  })

  const { isBelowMinimalAmount } = useMinimalAmount({
    value,
    minimalAmounts,
    baseCurrency,
    counterCurrency,
  })

  const { baseCurrencyOptions, counterCurrencyOptions } = useCurrencySelectOptions({
    baseCurrency,
    counterCurrency,
    supportedCurrenciesPairs,
  })

  useExchangeData({
    isSettingsLoading,
    direction,
    baseCurrency,
    counterCurrency,
    value,
    discountRate,
    supportedCurrenciesPairs,
    setExchangeData,
    setSpinnerError,
    isCurrencyPairSupported,
    setIsCurrentCurrencySupported,
    isBelowMinimalAmount,
    config: {
      DEFAULT_RESPONSE,
      DEFAULT_REFRESH_TIME,
      DEFAULT_EXCHANGE_DATA,
    },
  })

  return (
    <div className="calc-widget">
      <CalculatorHeader />
      <div className="calc-widget__main">
        <CalculatorActualRate
          baseCurrency={baseCurrency}
          counterCurrency={counterCurrency}
          exchangeData={exchangeData}
          allCurrencies={allCurrencies}
        />
        <div className="calc-widget__main-content">
          <div className="calc-widget__form">
            <CalculatorDirectionSelect
              direction={direction}
              setDirection={setDirection}
              options={FIXED_OPTION_LIST}
            />
            <CalculatorInput
              value={value}
              setValue={setValue}
              isBelowMinimalAmount={isBelowMinimalAmount}
              debounceTime={DEFAULT_DEBOUNCE_TIME}
            />
            <div className="calc-widget__currency-selects">
              <CalculatorCurrencySelect
                isBaseCurrencyComponent={true}
                baseCurrency={baseCurrency}
                counterCurrency={counterCurrency}
                handleSetbaseCurrency={setBaseCurrency}
                handleSetcounterCurrency={setCounterCurrency}
                fixedCurrencyList={baseCurrencyOptions}
              />
              <p className="calc-widget__for">{i18n('calculator.forBuy')}</p>
              <CalculatorCurrencySelect
                isBaseCurrencyComponent={false}
                baseCurrency={baseCurrency}
                counterCurrency={counterCurrency}
                handleSetbaseCurrency={setBaseCurrency}
                handleSetcounterCurrency={setCounterCurrency}
                fixedCurrencyList={counterCurrencyOptions}
              />
            </div>
          </div>
          {isBelowMinimalAmount && <p className="calc-widget__error-info">{i18n('calculatorForegin.minAmount')}</p>}
          {!isCurrentCurrencySupported && (
            <p className="calc-widget__error-info">{i18n('calculator.unsupportedPair')}</p>
          )}
        </div>
        <CalculatorFooter
          exchangeData={exchangeData}
          direction={direction}
          counterCurrency={counterCurrency}
          ctaVariant="button"
          lang={currentLang}
          discountRate={discountRate}
        />
      </div>
      {spinnerError.isErrorVisible && <ErrorSpinner errorText={spinnerError.message} />}
    </div>
  )
}

export default MainCalculatorView
